import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import useLocalStorage from "./useLocalStorage";
import * as api from "../services/api";

const LOCAL_KEY = "khronos_session_recipes";
const MAX_LOCAL_RECIPES = 50;

/**
 * Session recipes (issue #78) — named, reusable sparse partial planConfigs.
 *
 * Follows useWorkoutPresets: localStorage when signed out, the API when signed
 * in, optimistic CRUD either way. It adds one thing that hook does not do —
 * recipes created signed-out are promoted to the account on sign-in rather
 * than being stranded in localStorage, so "try it, then make an account" does
 * not silently lose your work.
 */
export default function useSessionRecipes() {
  const { isAuthenticated } = useAuth();

  const [localRecipes, setLocalRecipes] = useLocalStorage(LOCAL_KEY, []);

  const [remoteRecipes, setRemoteRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Promotion must run once per sign-in, not once per effect run — React
  // remounts effects in StrictMode, and a second pass would duplicate every
  // recipe into the account.
  const promotingRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      promotingRef.current = false;
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    const load = async () => {
      try {
        const existing = await api.getSessionRecipes();
        if (cancelled) return;

        const pending = promotingRef.current ? [] : localRecipes;
        if (pending.length === 0) {
          setRemoteRecipes(existing);
          return;
        }

        promotingRef.current = true;
        const promoted = [];
        for (const recipe of pending) {
          try {
            promoted.push(
              // eslint-disable-next-line no-await-in-loop
              await api.createSessionRecipe({
                name: recipe.name,
                config: recipe.config || {},
                // A local recipe can only ever hold an inline kit list: it was
                // created with no account, so there were no equipment presets
                // to reference.
                equipment_preset_id: null,
                is_default: false,
              }),
            );
          } catch (err) {
            console.error("Failed to promote session recipe:", err);
          }
        }

        if (cancelled) return;
        // Only clear what actually made it across, so a failed promotion can be
        // retried on the next sign-in instead of vanishing.
        if (promoted.length === pending.length) setLocalRecipes([]);
        setRemoteRecipes([...promoted, ...existing]);
      } catch (err) {
        if (!cancelled) setError("Failed to load session recipes");
        console.error("Failed to fetch session recipes:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // localRecipes is read as the promotion payload but must not retrigger the
    // load — the guard ref covers repeat runs, and re-fetching on every local
    // edit would fight the optimistic updates below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, setLocalRecipes]);

  const recipes = isAuthenticated ? remoteRecipes : localRecipes;

  const defaultRecipe = useMemo(
    () => recipes.find((r) => r.is_default) || null,
    [recipes],
  );

  const recentRecipes = useMemo(
    () =>
      [...recipes]
        .filter((r) => r.last_used_at)
        .sort((a, b) => new Date(b.last_used_at) - new Date(a.last_used_at))
        .slice(0, 5),
    [recipes],
  );

  const clearError = useCallback(() => setError(null), []);

  // ─── CRUD ──────────────────────────────────────────────

  const saveRecipe = useCallback(
    async ({ name, config, equipment_preset_id, is_default }) => {
      const now = new Date().toISOString();
      const payload = {
        name,
        config: config || {},
        equipment_preset_id: equipment_preset_id ?? null,
        is_default: is_default || false,
      };

      if (isAuthenticated) {
        const tempId = `temp-${Date.now()}`;
        const optimistic = {
          ...payload,
          id: tempId,
          last_used_at: null,
          created_at: now,
          updated_at: now,
        };
        setRemoteRecipes((prev) =>
          payload.is_default
            ? [optimistic, ...prev.map((r) => ({ ...r, is_default: false }))]
            : [optimistic, ...prev],
        );
        try {
          const created = await api.createSessionRecipe(payload);
          setRemoteRecipes((prev) =>
            prev.map((r) => (r.id === tempId ? created : r)),
          );
          return created;
        } catch (err) {
          setRemoteRecipes((prev) => prev.filter((r) => r.id !== tempId));
          setError("Failed to save recipe");
          throw err;
        }
      }

      const recipe = {
        ...payload,
        id: `local-${Date.now()}`,
        last_used_at: null,
        created_at: now,
        updated_at: now,
      };
      setLocalRecipes((prev) =>
        [
          recipe,
          ...(payload.is_default
            ? prev.map((r) => ({ ...r, is_default: false }))
            : prev),
        ].slice(0, MAX_LOCAL_RECIPES),
      );
      return recipe;
    },
    [isAuthenticated, setLocalRecipes],
  );

  const updateRecipe = useCallback(
    async (id, updates) => {
      // Setting a default clears the others — one default per user.
      const applyLocally = (list) =>
        list.map((r) => {
          if (r.id === id) {
            return { ...r, ...updates, updated_at: new Date().toISOString() };
          }
          return updates.is_default ? { ...r, is_default: false } : r;
        });

      if (isAuthenticated) {
        const previous = remoteRecipes;
        setRemoteRecipes(applyLocally);
        try {
          const updated = await api.updateSessionRecipe(id, updates);
          setRemoteRecipes((list) =>
            list.map((r) => (r.id === id ? updated : r)),
          );
          return updated;
        } catch (err) {
          setRemoteRecipes(previous);
          setError("Failed to update recipe");
          throw err;
        }
      }

      let result;
      setLocalRecipes((list) => {
        const next = applyLocally(list);
        result = next.find((r) => r.id === id);
        return next;
      });
      return result;
    },
    [isAuthenticated, remoteRecipes, setLocalRecipes],
  );

  const deleteRecipe = useCallback(
    async (id) => {
      if (isAuthenticated) {
        const previous = remoteRecipes;
        setRemoteRecipes((list) => list.filter((r) => r.id !== id));
        try {
          await api.deleteSessionRecipe(id);
        } catch (err) {
          setRemoteRecipes(previous);
          setError("Failed to delete recipe");
          throw err;
        }
      } else {
        setLocalRecipes((list) => list.filter((r) => r.id !== id));
      }
    },
    [isAuthenticated, remoteRecipes, setLocalRecipes],
  );

  const touchRecipe = useCallback(
    (id) => {
      const now = new Date().toISOString();
      if (isAuthenticated) {
        setRemoteRecipes((list) =>
          list.map((r) => (r.id === id ? { ...r, last_used_at: now } : r)),
        );
        // Fire-and-forget: a missed "last used" is not worth an error state.
        api.touchSessionRecipe(id).catch(() => {});
      } else {
        setLocalRecipes((list) =>
          list.map((r) => (r.id === id ? { ...r, last_used_at: now } : r)),
        );
      }
    },
    [isAuthenticated, setLocalRecipes],
  );

  return {
    recipes,
    defaultRecipe,
    recentRecipes,
    loading,
    error,
    clearError,
    saveRecipe,
    updateRecipe,
    deleteRecipe,
    touchRecipe,
  };
}
